import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDebounce} from '../../shared/hooks';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {Building2, ChevronLeft, ChevronRight, Filter, MapPin, Package, Plus, Trash2, X} from 'lucide-react';
import {useNavigate} from 'react-router-dom';
import {PageHeader} from '../../shared/ui';
import {useEmpresas} from './useEmpresas';
import {createEmpresa, deleteEmpresa, getEmpresasFiltered, updateEmpresa} from './EmpresaService';
import {formatCpfCnpj, getBackendWarningMessage, onlyDigits, sanitizeCep, searchCep} from '../../shared/utils';
import {getEmpresasMenus} from './empresasMenu';
import {DIAS_DO_CICLO, enderecoVazio, explicarCiclo, validarEmpresa} from './empresasUtils';
import AssinaturasDaEmpresa from './components/AssinaturasDaEmpresa';

const emptyAddress = {
    tipo: 'PRINCIPAL',
    principal: true,
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    cep: '',
    code_ibge_cidade: '',
    code_ibge_uf: '',
    latitude: '',
    longitude: '',
};

const tiposEndereco = [
    {value: 'PRINCIPAL', label: 'Principal'},
    {value: 'COBRANCA', label: 'Cobrança'},
    {value: 'FILIAL', label: 'Filial'},
    {value: 'ENTREGA', label: 'Entrega'},
    {value: 'CORRESPONDENCIA', label: 'Correspondência'},
];

const estadosBrasileiros = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
    'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

const createEmptyEmpresa = () => ({
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    inscricao_estadual: 'ISENTO',
    email: '',
    telefone: '',
    enderecos: [{...emptyAddress}],
    responsavel: '',
    status: 'ATIVO',
    dia_fechamento: 1,
    dia_vencimento: 10,
});

const getPrimaryAddress = (empresa) => (
    empresa?.enderecos?.find((endereco) => endereco.principal) || empresa?.enderecos?.[0] || emptyAddress
);

const normalizeEmpresa = (empresa) => {
    const enderecos = Array.isArray(empresa?.enderecos) && empresa.enderecos.length
        ? empresa.enderecos
        : [getPrimaryAddress(empresa)];

    return {
        ...createEmptyEmpresa(),
        ...empresa,
        razao_social: empresa?.razao_social || empresa?.nome || '',
        nome_fantasia: empresa?.nome_fantasia || empresa?.nome || '',
        email: empresa?.email || empresa?.username || '',
        status: empresa?.status || (empresa?.ativo === false ? 'INATIVO' : 'ATIVO'),
        dia_fechamento: Number(empresa?.dia_fechamento || empresa?.dia_fechamento_fatura || 1),
        dia_vencimento: Number(empresa?.dia_vencimento || empresa?.dia_vencimento_fatura || 10),
        enderecos: enderecos.map((endereco, index) => ({
            ...emptyAddress,
            ...endereco,
            tipo: endereco?.tipo || (index === 0 ? 'PRINCIPAL' : 'FILIAL'),
            principal: endereco?.principal ?? index === 0,
        })),
    };
};

const formatPhone = (value = '') => {
    const digits = onlyDigits(value);
    if (!digits) return '';
    if (digits.length <= 2) return `(${digits}`;

    const ddd = digits.slice(0, 2);
    const phone = digits.slice(2);

    if (digits.length <= 10) {
        if (phone.length <= 4) return `(${ddd}) ${phone}`;
        return `(${ddd}) ${phone.slice(0, 4)}-${phone.slice(4)}`;
    }

    const extra = digits.length > 11 ? ` ${digits.slice(11)}` : '';
    return `(${ddd}) ${phone.slice(0, 5)}-${phone.slice(5, 9)}${extra}`;
};

const getPhoneValidationMessage = (value = '') => {
    const digits = onlyDigits(value);
    if (!digits) return '';
    if (digits.length < 10) return 'Informe o DDD do telefone';
    if (digits.length > 11) return 'Telefone deve ter no máximo 11 dígitos';
    return '';
};

const getPaginatedResult = (response) => {
    const value = response?.data?.valor || response?.data || {};
    const items = value.items || [];

    return {
        items,
        total: items.length,
    };
};

const getEnderecoPrincipal = (empresa) => {
    const principal = empresa?.enderecos?.find((item) => item.principal) || empresa?.enderecos?.[0];
    if (!principal) return '-';
    if (!principal.cidade && !principal.uf) return '-';
    return `${principal.cidade || '-'}${principal.uf ? `/${principal.uf}` : ''}`;
};

const buildEmpresaPayload = (empresa) => {
    const enderecos = Array.isArray(empresa.enderecos) && empresa.enderecos.length ? empresa.enderecos : [getPrimaryAddress(empresa)];
    const hasPrincipalAddress = enderecos.some((item) => item.principal);

    return {
        razao_social: empresa.razao_social?.trim(),
        nome_fantasia: empresa.nome_fantasia?.trim(),
        cnpj: onlyDigits(empresa.cnpj),
        inscricao_estadual: empresa.inscricao_estadual?.trim() || 'ISENTO',
        email: empresa.email?.trim(),
        telefone: onlyDigits(empresa.telefone),
        enderecos: enderecos.map((endereco, index) => ({
            tipo: endereco.tipo || (index === 0 ? 'PRINCIPAL' : 'FILIAL'),
            principal: Boolean(endereco.principal || (index === 0 && !hasPrincipalAddress)),
            cep: endereco.cep?.replace(/\D/g, ''),
            logradouro: endereco.logradouro?.trim(),
            numero: endereco.numero?.trim(),
            complemento: endereco.complemento?.trim(),
            bairro: endereco.bairro?.trim(),
            cidade: endereco.cidade?.trim(),
            uf: endereco.uf?.trim().toUpperCase(),
            code_ibge_cidade: endereco.code_ibge_cidade?.trim(),
            code_ibge_uf: endereco.code_ibge_uf?.trim(),
            latitude: endereco.latitude?.trim(),
            longitude: endereco.longitude?.trim(),
        })),
        responsavel: empresa.responsavel?.trim(),
        status: empresa.status || 'ATIVO',
        // A forma de pagamento não é da empresa desde a BBF-137: fica em cada assinatura.
        dia_fechamento: Number(empresa.dia_fechamento),
        dia_vencimento: Number(empresa.dia_vencimento),
    };
};

const statusColors = {
    ATIVO: {bg: '#dcfce7', text: '#15803d'},
    INATIVO: {bg: '#f3f4f6', text: '#374151'},
    BLOQUEADO: {bg: '#fee2e2', text: '#b91c1c'},
};

const SectionTitle = ({title}) => (
    <Typography sx={{fontSize: '1.125rem', color: '#111827', fontWeight: 600, mb: 2}}>
        {title}
    </Typography>
);

const FieldLabel = ({children, required = false}) => (
    <Typography component="label" variant="body2" sx={{display: 'block', color: '#374151', mb: 1}}>
        {children} {required ? <Box component="span" sx={{color: '#ef4444'}}>*</Box> : null}
    </Typography>
);

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        bgcolor: '#fff',
        '&.Mui-focused fieldset': {
            borderColor: '#14b8a6',
            borderWidth: 2,
        },
    },
};

export default function EmpresasPage({openParent = true}) {
    const theme = useTheme();
    const navigate = useNavigate();
    const [open, setOpen] = useState(openParent);
    const [warn, setWarn] = useState('');
    const [statusFilter, setStatusFilter] = useState('todos');
    const [searchingCep, setSearchingCep] = useState(false);
    const [viewOnly, setViewOnly] = useState(false);
    const [dialogTab, setDialogTab] = useState('dados');
    const [enderecoExpandido, setEnderecoExpandido] = useState(0);
    const lastCepRef = useRef('');
    const logradouroInputRef = useRef(null);
    const numeroInputRef = useRef(null);
    const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        rows,
        page,
        rowsPerPage,
        total,
        filter,
        sortBy,
        order,
        loading,
        error,
        ok,
        saving,
        empresa,
        dialogOpen,
        confirmId,
        editingObject: editing,
        setRows,
        setPage,
        setRowsPerPage,
        setTotal,
        setFilter,
        setLoading: setLoadingPage,
        setError: setErrorPage,
        setOk,
        setSaving,
        setEmpresa,
        setDialogOpen,
        setConfirmId,
        setEditingObject,
    } = useEmpresas();
    const debounced = useDebounce(filter, 400);

    useEffect(() => {
        if (!rowsPerPage || rowsPerPage !== 8) setRowsPerPage(8);
    }, [rowsPerPage, setRowsPerPage]);

    const handleRequestError = useCallback((requestError, fallbackMessage) => {
        const backendMessage = getBackendWarningMessage(requestError);
        if (backendMessage) {
            setWarn(backendMessage);
            return;
        }
        setErrorPage(fallbackMessage);
    }, [setErrorPage]);

    const fetchRows = useCallback(async () => {
        try {
            setLoadingPage(true);
            const response = await getEmpresasFiltered({
                page: 1,
                pageSize: 1000,
                filter: debounced,
                sortBy,
                order,
            });
            const result = getPaginatedResult(response);
            setRows(result.items);
            setTotal(result.total);
        } catch (requestError) {
            handleRequestError(requestError, 'Falha ao carregar empresas.');
        } finally {
            setLoadingPage(false);
        }
    }, [debounced, handleRequestError, order, setLoadingPage, setRows, setTotal, sortBy]);

    useEffect(() => {
        fetchRows();
    }, [fetchRows]);

    const filteredRows = useMemo(() => rows.filter((row) => (
        statusFilter === 'todos' || (row.status || (row.ativo === false ? 'INATIVO' : 'ATIVO')) === statusFilter
    )), [rows, statusFilter]);

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
    const currentPage = Math.min(page + 1, totalPages);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedRows = filteredRows.slice(startIndex, startIndex + rowsPerPage);

    useEffect(() => {
        if (currentPage !== page + 1) {
            setPage(Math.max(0, totalPages - 1));
        }
    }, [currentPage, page, setPage, totalPages]);

    const openCreate = () => {
        setViewOnly(false);
        setDialogTab('dados');
        setEnderecoExpandido(0);
        setEditingObject(null);
        setEmpresa(createEmptyEmpresa());
        setDialogOpen(true);
    };

    const openEdit = (row) => {
        setViewOnly(false);
        setDialogTab('dados');
        setEnderecoExpandido(0);
        setEditingObject(row);
        setEmpresa(normalizeEmpresa(row));
        setDialogOpen(true);
    };

    const openView = (row) => {
        setViewOnly(true);
        setDialogTab('dados');
        setEnderecoExpandido(0);
        setEditingObject(row);
        setEmpresa(normalizeEmpresa(row));
        setDialogOpen(true);
    };

    const updateEmpresaField = (field, value) => {
        setEmpresa((current) => ({...normalizeEmpresa(current), [field]: value}));
    };

    const updateEnderecoField = (index, field, value) => {
        setEmpresa((current) => {
            const normalized = normalizeEmpresa(current);
            const enderecos = normalized.enderecos.map((endereco, enderecoIndex) => {
                if (enderecoIndex !== index) {
                    return field === 'principal' && value === true ? {...endereco, principal: false} : endereco;
                }

                return {
                    ...endereco,
                    [field]: field === 'uf' ? value.toUpperCase().slice(0, 2) : value,
                };
            });

            return {
                ...normalized,
                enderecos,
            };
        });
    };

    const updateEnderecoFields = (index, fields) => {
        setEmpresa((current) => {
            const normalized = normalizeEmpresa(current);
            const enderecos = normalized.enderecos.map((endereco, enderecoIndex) => (
                enderecoIndex === index
                    ? {
                        ...endereco,
                        ...fields,
                        uf: fields.uf ? fields.uf.toUpperCase().slice(0, 2) : endereco.uf,
                    }
                    : endereco
            ));

            return {
                ...normalized,
                enderecos,
            };
        });
    };

    const addEndereco = () => {
        setEmpresa((current) => {
            const normalized = normalizeEmpresa(current);
            return {
                ...normalized,
                enderecos: [
                    ...normalized.enderecos,
                    {...emptyAddress, tipo: 'FILIAL', principal: false},
                ],
            };
        });
        setEnderecoExpandido(normalizeEmpresa(empresa).enderecos.length);
    };

    const removeEndereco = (index) => {
        const normalized = normalizeEmpresa(empresa);
        if (normalized.enderecos.length === 1) {
            setWarn('É necessário ter pelo menos um endereço cadastrado.');
            return;
        }

        const enderecos = normalized.enderecos.filter((_, enderecoIndex) => enderecoIndex !== index);
        if (!enderecos.some((endereco) => endereco.principal)) {
            enderecos[0] = {...enderecos[0], principal: true};
        }
        setEmpresa({...normalized, enderecos});
        setEnderecoExpandido(0);
    };

    const lookupCep = async (index, rawCep) => {
        const cep = sanitizeCep(rawCep);
        const lookupKey = `${index}:${cep}`;
        if (cep.length !== 8 || lookupKey === lastCepRef.current) return;

        lastCepRef.current = lookupKey;
        setSearchingCep(true);

        try {
            updateEnderecoFields(index, await searchCep(cep));
            numeroInputRef.current?.focus();
        } catch {
            setErrorPage('Não foi possível consultar o CEP. Informe o logradouro manualmente.');
            logradouroInputRef.current?.focus();
        } finally {
            setSearchingCep(false);
        }
    };

    const handleCepChange = (index, value) => {
        updateEnderecoField(index, 'cep', value);
        const cep = sanitizeCep(value);
        if (cep.length === 8) {
            lookupCep(index, cep);
        } else {
            lastCepRef.current = '';
        }
    };

    // Empresa que já existe e nunca teve endereço (criada pela API): o bloco vazio do formulário não é exigido.
    const enderecoOpcional = Boolean(editing?.id) && !(editing.enderecos || []).length;

    // As regras do app-empresas, conferidas antes de ir ao backend (BBF-148).
    const validate = (formValue) => {
        const value = normalizeEmpresa(formValue);
        return validarEmpresa(value, getPrimaryAddress(value), {enderecoOpcional});
    };

    const onSave = async () => {
        const errors = validate(empresa);
        if (Object.keys(errors).length > 0) {
            setErrorPage(Object.values(errors)[0]);
            return;
        }

        try {
            setSaving(true);
            const payload = buildEmpresaPayload(normalizeEmpresa(empresa));
            // O app-empresas confere os quatro campos de todo endereço enviado: o bloco vazio não vai.
            if (enderecoOpcional) payload.enderecos = payload.enderecos.filter((endereco) => !enderecoVazio(endereco));
            if (editing?.id) {
                await updateEmpresa(editing.id, payload);
                setOk('Empresa atualizada com sucesso.');
            } else {
                await createEmpresa(payload);
                setOk('Empresa criada com sucesso.');
            }
            setDialogOpen(false);
            await fetchRows();
        } catch (requestError) {
            handleRequestError(requestError, 'Não foi possível salvar a empresa.');
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async () => {
        if (!confirmId) return;

        try {
            await deleteEmpresa(confirmId);
            setOk('Empresa excluída.');
            setConfirmId(null);
            await fetchRows();
        } catch (requestError) {
            handleRequestError(requestError, 'Falha ao excluir empresa.');
        }
    };

    return (
        <Box sx={{display: 'flex', minHeight: '100dvh'}}>
            <PageHeader
                title="CRM"
                titleAction={() => navigate('/')}
                itemsMenu={getEmpresasMenus(navigate, 'gestao')}
                onHandleMenuOpen={() => null}
                onHandleDrawerToggle={setOpen}
                openParent={open}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: {xs: 2, md: 3},
                    minWidth: 0,
                    boxSizing: 'border-box',
                    transition: theme.transitions.create(['margin', 'width']),
                    mt: 8,
                    backgroundColor: '#f9fafb',
                    minHeight: 'calc(100dvh - 64px)',
                    position: 'relative',
                }}
            >
                <Stack spacing={2.5}>
                    <Paper variant="outlined" sx={{px: {xs: 2, md: 3}, py: {xs: 2, md: 2.5}, borderRadius: 2}}>
                        <Typography variant="h4" fontWeight={700}>Clientes</Typography>
                        <Typography color="text.secondary">
                            Gerencie seus clientes, assinaturas e relacionamento comercial.
                        </Typography>
                    </Paper>

                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} sx={{mt: 0.5}}>
                        <TextField
                            placeholder="Buscar por razão social, nome fantasia ou CNPJ..."
                            value={filter}
                            onChange={(event) => {
                                setFilter(event.target.value);
                                setPage(0);
                            }}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{opacity: 0.65}} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            select
                            value={statusFilter}
                            onChange={(event) => {
                                setStatusFilter(event.target.value);
                                setPage(0);
                            }}
                            sx={{minWidth: {sm: 210}}}
                        >
                            <MenuItem value="todos">Todos os status</MenuItem>
                            <MenuItem value="ATIVO">Ativo</MenuItem>
                            <MenuItem value="INATIVO">Inativo</MenuItem>
                            <MenuItem value="BLOQUEADO">Bloqueado</MenuItem>
                        </TextField>
                        <Button variant="outlined" startIcon={<Filter size={18} />} sx={{minWidth: 146, bgcolor: 'background.paper'}}>
                            Mais Filtros
                        </Button>
                    </Stack>

                    <Paper variant="outlined" sx={{borderRadius: 2, overflow: 'hidden'}}>
                        <Box sx={{overflowX: 'auto'}}>
                            <Box component="table" sx={{width: '100%', borderCollapse: 'collapse', minWidth: 980}}>
                                <Box component="thead">
                                    <Box component="tr" sx={{bgcolor: '#f9fafb'}}>
                                        <Box component="th" sx={{px: 2.5, py: 1.5, textAlign: 'left', color: '#4b5563', fontWeight: 500, fontSize: 14}}>Cliente</Box>
                                        <Box component="th" sx={{px: 2.5, py: 1.5, textAlign: 'left', color: '#4b5563', fontWeight: 500, fontSize: 14}}>CPF/CNPJ</Box>
                                        <Box component="th" sx={{px: 2.5, py: 1.5, textAlign: 'left', color: '#4b5563', fontWeight: 500, fontSize: 14}}>Responsável</Box>
                                        <Box component="th" sx={{px: 2.5, py: 1.5, textAlign: 'left', color: '#4b5563', fontWeight: 500, fontSize: 14}}>Telefone</Box>
                                        <Box component="th" sx={{px: 2.5, py: 1.5, textAlign: 'left', color: '#4b5563', fontWeight: 500, fontSize: 14}}>Cidade/UF</Box>
                                        <Box component="th" sx={{px: 2.5, py: 1.5, textAlign: 'left', color: '#4b5563', fontWeight: 500, fontSize: 14}}>Status</Box>
                                        <Box component="th" sx={{px: 2.5, py: 1.5, textAlign: 'left', color: '#4b5563', fontWeight: 500, fontSize: 14}}>Ciclo</Box>
                                        <Box component="th" sx={{px: 2.5, py: 1.5, textAlign: 'right', color: '#4b5563', fontWeight: 500, fontSize: 14}}>Ações</Box>
                                    </Box>
                                </Box>
                                <Box component="tbody">
                                    {loading ? (
                                        <Box component="tr">
                                            <Box component="td" colSpan={8} sx={{py: 6, textAlign: 'center'}}>
                                                <CircularProgress size={20} />
                                            </Box>
                                        </Box>
                                    ) : null}

                                    {!loading && !paginatedRows.length ? (
                                        <Box component="tr">
                                            <Box component="td" colSpan={8} sx={{py: 6, textAlign: 'center', color: 'text.secondary'}}>
                                                Nenhum cliente encontrado.
                                            </Box>
                                        </Box>
                                    ) : null}

                                    {paginatedRows.map((row) => {
                                        const status = row.status || (row.ativo === false ? 'INATIVO' : 'ATIVO');
                                        const colors = statusColors[status] || statusColors.INATIVO;

                                        return (
                                            <Box component="tr" key={row.id} sx={{'&:hover': {bgcolor: '#f9fafb'}}}>
                                                <Box component="td" sx={{px: 2.5, py: 1.5, borderTop: '1px solid #e5e7eb'}}>
                                                    <Typography>{row.nome_fantasia || row.razao_social || row.nome || '-'}</Typography>
                                                    {row.nome_fantasia ? (
                                                        <Typography variant="caption" color="text.secondary">{row.razao_social}</Typography>
                                                    ) : null}
                                                </Box>
                                                <Box component="td" sx={{px: 2.5, py: 1.5, borderTop: '1px solid #e5e7eb'}}>
                                                    <Box component="code" sx={{fontSize: 13}}>{formatCpfCnpj(row.cnpj)}</Box>
                                                </Box>
                                                <Box component="td" sx={{px: 2.5, py: 1.5, borderTop: '1px solid #e5e7eb'}}>{row.responsavel || '-'}</Box>
                                                <Box component="td" sx={{px: 2.5, py: 1.5, borderTop: '1px solid #e5e7eb'}}>{formatPhone(row.telefone) || '-'}</Box>
                                                <Box component="td" sx={{px: 2.5, py: 1.5, borderTop: '1px solid #e5e7eb'}}>{getEnderecoPrincipal(row)}</Box>
                                                <Box component="td" sx={{px: 2.5, py: 1.5, borderTop: '1px solid #e5e7eb'}}>
                                                    <Chip size="small" label={status} sx={{bgcolor: colors.bg, color: colors.text, fontWeight: 600, borderRadius: 10}} />
                                                </Box>
                                                <Box component="td" sx={{px: 2.5, py: 1.5, borderTop: '1px solid #e5e7eb'}}>
                                                    <Typography variant="body2">Fecha no dia {row.dia_fechamento || '-'}</Typography>
                                                    <Typography variant="caption" color="text.secondary">Vence no dia {row.dia_vencimento || '-'}</Typography>
                                                </Box>
                                                <Box component="td" sx={{px: 2.5, py: 1.5, borderTop: '1px solid #e5e7eb'}}>
                                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                        <IconButton size="small" sx={{color: '#0f766e', '&:hover': {bgcolor: '#ccfbf1'}}} onClick={() => openView(row)}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" sx={{color: '#0f766e', '&:hover': {bgcolor: '#ccfbf1'}}} onClick={() => openEdit(row)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" sx={{color: '#dc2626', '&:hover': {bgcolor: '#fee2e2'}}} onClick={() => setConfirmId(row.id)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Box>

                        <Stack
                            direction={{xs: 'column', md: 'row'}}
                            justifyContent="space-between"
                            alignItems={{md: 'center'}}
                            spacing={1}
                            sx={{px: 2.5, py: 1.5, borderTop: '1px solid #e5e7eb', bgcolor: 'background.paper'}}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Mostrando {filteredRows.length ? startIndex + 1 : 0} a {Math.min(startIndex + rowsPerPage, filteredRows.length)} de {filteredRows.length || total} clientes
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <IconButton
                                    onClick={() => setPage(Math.max(0, currentPage - 2))}
                                    disabled={currentPage <= 1}
                                    sx={{border: '1px solid #d1d5db', borderRadius: 1}}
                                >
                                    <ChevronLeft size={18} />
                                </IconButton>
                                <Typography variant="body2" color="text.secondary">
                                    Página {currentPage} de {totalPages}
                                </Typography>
                                <IconButton
                                    onClick={() => setPage(Math.min(totalPages - 1, currentPage))}
                                    disabled={currentPage >= totalPages}
                                    sx={{border: '1px solid #d1d5db', borderRadius: 1}}
                                >
                                    <ChevronRight size={18} />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Paper>
                </Stack>

                <Box sx={{position: 'fixed', right: 32, bottom: 32}}>
                    <IconButton
                        onClick={openCreate}
                        sx={{
                            width: 56,
                            height: 56,
                            bgcolor: '#0f766e',
                            color: '#fff',
                            boxShadow: 4,
                            '&:hover': {bgcolor: '#115e59'},
                        }}
                    >
                        <Plus size={24} />
                    </IconButton>
                </Box>

                <Dialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    maxWidth="lg"
                    fullWidth
                    fullScreen={fullScreenDialog}
                    PaperProps={{
                        sx: {
                            borderRadius: fullScreenDialog ? 0 : 2,
                            maxHeight: fullScreenDialog ? '100dvh' : '90vh',
                            overflow: 'hidden',
                        },
                    }}
                >
                    <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', p: 3, borderBottom: '1px solid #e5e7eb'}}>
                        <Box>
                            <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
                                <Typography sx={{fontSize: {xs: '1.5rem', md: '1.75rem'}, color: '#111827', fontWeight: 600}}>
                                    {viewOnly ? 'Visualizar Cliente' : editing ? 'Editar Cliente' : 'Novo Cliente'}
                                </Typography>
                                {viewOnly ? (
                                    <Chip label="Somente Leitura" size="small" sx={{bgcolor: '#dbeafe', color: '#1d4ed8', borderRadius: 10}} />
                                ) : null}
                            </Stack>
                            {editing || viewOnly ? (
                                <Typography variant="body2" sx={{color: '#6b7280', mt: 0.5}}>
                                    {empresa?.nome_fantasia || empresa?.razao_social}
                                    {empresa?.nome_fantasia && empresa?.razao_social ? ` - ${empresa.razao_social}` : ''}
                                </Typography>
                            ) : null}
                        </Box>
                        <IconButton onClick={() => setDialogOpen(false)} sx={{borderRadius: 2}}>
                            <X size={20} color="#6b7280" />
                        </IconButton>
                    </Box>

                    {editing?.id ? (
                        <Box sx={{borderBottom: '1px solid #e5e7eb', px: 3}}>
                            <Stack direction="row" spacing={0.5} sx={{overflowX: 'auto'}}>
                                {[
                                    {id: 'dados', label: 'Dados Gerais', icon: Building2},
                                    {id: 'assinaturas', label: 'Assinaturas e próxima fatura', icon: Package},
                                ].map((tab) => {
                                    const Icon = tab.icon;
                                    const selected = dialogTab === tab.id;

                                    return (
                                        <Button
                                            key={tab.id}
                                            onClick={() => setDialogTab(tab.id)}
                                            startIcon={<Icon size={18} />}
                                            sx={{
                                                position: 'relative',
                                                px: 2,
                                                py: 1.5,
                                                color: selected ? '#0f766e' : '#6b7280',
                                                textTransform: 'none',
                                                whiteSpace: 'nowrap',
                                                '&:hover': {bgcolor: 'transparent', color: '#111827'},
                                            }}
                                        >
                                            {tab.label}
                                            {selected ? <Box sx={{position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, bgcolor: '#0f766e'}} /> : null}
                                        </Button>
                                    );
                                })}
                            </Stack>
                        </Box>
                    ) : null}

                    <Box component="form" onSubmit={(event) => {
                        event.preventDefault();
                        if (!viewOnly) onSave();
                    }} sx={{display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1}}>
                        <DialogContent sx={{p: 0, overflowY: 'auto'}}>
                            <Box sx={{p: 3}}>
                                {dialogTab === 'assinaturas' && editing?.id ? (
                                    <AssinaturasDaEmpresa empresa={editing} />
                                ) : (
                                    <Stack spacing={3}>
                                        <Box>
                                            <SectionTitle title="Identificação" />
                                            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2}}>
                                                <Box>
                                                    <FieldLabel required>Razão Social</FieldLabel>
                                                    <TextField autoFocus value={empresa?.razao_social || ''} onChange={(event) => updateEmpresaField('razao_social', event.target.value)} placeholder="Ex: Agropecuaria Vale Verde LTDA" disabled={viewOnly} fullWidth sx={inputSx} />
                                                </Box>
                                                <Box>
                                                    <FieldLabel>Nome Fantasia</FieldLabel>
                                                    <TextField value={empresa?.nome_fantasia || ''} onChange={(event) => updateEmpresaField('nome_fantasia', event.target.value)} placeholder="Ex: Vale Verde Agro" disabled={viewOnly} fullWidth sx={inputSx} />
                                                </Box>
                                                <Box>
                                                    <FieldLabel required>CPF/CNPJ</FieldLabel>
                                                    <TextField value={formatCpfCnpj(empresa?.cnpj)} onChange={(event) => updateEmpresaField('cnpj', formatCpfCnpj(event.target.value))} placeholder="00.000.000/0000-00" disabled={viewOnly} fullWidth sx={inputSx} />
                                                </Box>
                                                <Box>
                                                    <FieldLabel>Inscrição Estadual</FieldLabel>
                                                    <TextField value={empresa?.inscricao_estadual || ''} onChange={(event) => updateEmpresaField('inscricao_estadual', event.target.value)} placeholder="000000000" disabled={viewOnly} fullWidth sx={inputSx} />
                                                </Box>
                                                <Box>
                                                    <FieldLabel required>Status</FieldLabel>
                                                    <TextField select value={empresa?.status || 'ATIVO'} onChange={(event) => updateEmpresaField('status', event.target.value)} disabled={viewOnly} fullWidth sx={inputSx}>
                                                        <MenuItem value="ATIVO">ATIVO</MenuItem>
                                                        <MenuItem value="INATIVO">INATIVO</MenuItem>
                                                        <MenuItem value="BLOQUEADO">BLOQUEADO</MenuItem>
                                                    </TextField>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Box>
                                            <SectionTitle title="Contato" />
                                            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(3, 1fr)'}, gap: 2}}>
                                                <Box>
                                                    <FieldLabel>E-mail</FieldLabel>
                                                    <TextField type="email" value={empresa?.email || ''} onChange={(event) => updateEmpresaField('email', event.target.value)} placeholder="contato@empresa.com" disabled={viewOnly} fullWidth sx={inputSx} />
                                                </Box>
                                                <Box>
                                                    <FieldLabel>Telefone</FieldLabel>
                                                    <TextField value={formatPhone(empresa?.telefone)} onChange={(event) => updateEmpresaField('telefone', formatPhone(event.target.value))} placeholder="(00) 00000-0000" error={Boolean(getPhoneValidationMessage(empresa?.telefone))} helperText={getPhoneValidationMessage(empresa?.telefone)} disabled={viewOnly} fullWidth sx={inputSx} />
                                                </Box>
                                                <Box>
                                                    <FieldLabel>Responsável</FieldLabel>
                                                    <TextField value={empresa?.responsavel || ''} onChange={(event) => updateEmpresaField('responsavel', event.target.value)} placeholder="Nome do responsável" disabled={viewOnly} fullWidth sx={inputSx} />
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Box>
                                            <SectionTitle title="Ciclo de cobrança" />
                                            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2}}>
                                                <Box>
                                                    <FieldLabel required>Dia de fechamento</FieldLabel>
                                                    <TextField select value={empresa?.dia_fechamento || 1} onChange={(event) => updateEmpresaField('dia_fechamento', Number(event.target.value))} disabled={viewOnly} fullWidth helperText="Todas as assinaturas da empresa fecham neste dia, e a fatura sai nele." sx={inputSx} SelectProps={{inputProps: {'aria-label': 'Dia de fechamento'}}}>
                                                        {DIAS_DO_CICLO.map((dia) => <MenuItem key={dia} value={dia}>Dia {dia}</MenuItem>)}
                                                    </TextField>
                                                </Box>
                                                <Box>
                                                    <FieldLabel required>Dia de vencimento</FieldLabel>
                                                    <TextField select value={empresa?.dia_vencimento || 10} onChange={(event) => updateEmpresaField('dia_vencimento', Number(event.target.value))} disabled={viewOnly} fullWidth helperText="O boleto vence no primeiro dia de vencimento depois do fechamento." sx={inputSx} SelectProps={{inputProps: {'aria-label': 'Dia de vencimento'}}}>
                                                        {DIAS_DO_CICLO.map((dia) => <MenuItem key={dia} value={dia}>Dia {dia}</MenuItem>)}
                                                    </TextField>
                                                </Box>
                                                <Box sx={{gridColumn: {md: 'span 2'}, p: 1.5, borderRadius: 2, bgcolor: '#f0fdfa', border: '1px solid #99f6e4'}}>
                                                    <Typography variant="body2" sx={{color: '#0f766e'}}>
                                                        {explicarCiclo(empresa?.dia_fechamento || 1, empresa?.dia_vencimento || 10)} A forma de pagamento fica em cada assinatura.
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Box>
                                            <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems={{xs: 'flex-start', sm: 'center'}} spacing={1.5} sx={{mb: 2}}>
                                                <SectionTitle title="Endereços" />
                                                <Button type="button" onClick={addEndereco} disabled={viewOnly} startIcon={<Plus size={16} />} sx={{bgcolor: '#f0fdfa', color: '#0f766e', textTransform: 'none', '&:hover': {bgcolor: '#ccfbf1'}}}>
                                                    Adicionar Endereço
                                                </Button>
                                            </Stack>

                                            <Stack spacing={1.5}>
                                                {normalizeEmpresa(empresa).enderecos.map((endereco, index) => (
                                                    <Box key={`${endereco.tipo}-${index}`} sx={{border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden'}}>
                                                        <Box onClick={() => setEnderecoExpandido(enderecoExpandido === index ? -1 : index)} sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, cursor: 'pointer', '&:hover': {bgcolor: '#f9fafb'}}}>
                                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                                <MapPin size={20} color="#9ca3af" />
                                                                <Box>
                                                                    <Typography sx={{color: '#111827'}}>{endereco.tipo || 'Endereço'} {endereco.principal ? '(Principal)' : ''}</Typography>
                                                                    {endereco.cidade && endereco.uf ? (
                                                                        <Typography variant="body2" sx={{color: '#6b7280'}}>{endereco.logradouro}, {endereco.numero} - {endereco.cidade}/{endereco.uf}</Typography>
                                                                    ) : null}
                                                                </Box>
                                                            </Stack>
                                                            {normalizeEmpresa(empresa).enderecos.length > 1 ? (
                                                                <IconButton onClick={(event) => { event.stopPropagation(); removeEndereco(index); }} disabled={viewOnly} sx={{color: '#dc2626'}}>
                                                                    <Trash2 size={16} />
                                                                </IconButton>
                                                            ) : null}
                                                        </Box>

                                                        {enderecoExpandido === index ? (
                                                            <Box sx={{p: 2, borderTop: '1px solid #e5e7eb', bgcolor: '#f9fafb'}}>
                                                                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(4, 1fr)'}, gap: 2}}>
                                                                    <Box sx={{gridColumn: {md: 'span 2'}}}>
                                                                        <FieldLabel>Tipo</FieldLabel>
                                                                        <TextField select value={endereco.tipo || 'PRINCIPAL'} onChange={(event) => updateEnderecoField(index, 'tipo', event.target.value)} disabled={viewOnly} fullWidth sx={inputSx}>
                                                                            {tiposEndereco.map((tipo) => <MenuItem key={tipo.value} value={tipo.value}>{tipo.label}</MenuItem>)}
                                                                        </TextField>
                                                                    </Box>
                                                                    <Stack direction="row" alignItems="center" sx={{gridColumn: {md: 'span 2'}, pt: {md: 3.75}}}>
                                                                        <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: viewOnly ? 'default' : 'pointer'}}>
                                                                            <input type="checkbox" checked={Boolean(endereco.principal)} onChange={(event) => updateEnderecoField(index, 'principal', event.target.checked)} disabled={viewOnly} />
                                                                            <Typography variant="body2" sx={{color: '#374151'}}>Endereço Principal</Typography>
                                                                        </label>
                                                                    </Stack>
                                                                    <Box>
                                                                        <FieldLabel>CEP</FieldLabel>
                                                                        <TextField value={endereco.cep || ''} onChange={(event) => handleCepChange(index, event.target.value)} placeholder="00000-000" helperText={searchingCep && enderecoExpandido === index ? 'Buscando CEP...' : ''} disabled={viewOnly} fullWidth sx={inputSx} />
                                                                    </Box>
                                                                    <Box sx={{gridColumn: {md: 'span 3'}}}>
                                                                        <FieldLabel>Logradouro</FieldLabel>
                                                                        <TextField inputRef={logradouroInputRef} value={endereco.logradouro || ''} onChange={(event) => updateEnderecoField(index, 'logradouro', event.target.value)} placeholder="Rua, Avenida, etc" disabled={viewOnly} fullWidth sx={inputSx} />
                                                                    </Box>
                                                                    <Box>
                                                                        <FieldLabel>Número</FieldLabel>
                                                                        <TextField inputRef={numeroInputRef} value={endereco.numero || ''} onChange={(event) => updateEnderecoField(index, 'numero', event.target.value)} placeholder="000" disabled={viewOnly} fullWidth sx={inputSx} />
                                                                    </Box>
                                                                    <Box sx={{gridColumn: {md: 'span 2'}}}>
                                                                        <FieldLabel>Complemento</FieldLabel>
                                                                        <TextField value={endereco.complemento || ''} onChange={(event) => updateEnderecoField(index, 'complemento', event.target.value)} placeholder="Sala, Andar, etc" disabled={viewOnly} fullWidth sx={inputSx} />
                                                                    </Box>
                                                                    <Box>
                                                                        <FieldLabel>Bairro</FieldLabel>
                                                                        <TextField value={endereco.bairro || ''} onChange={(event) => updateEnderecoField(index, 'bairro', event.target.value)} placeholder="Bairro" disabled={viewOnly} fullWidth sx={inputSx} />
                                                                    </Box>
                                                                    <Box sx={{gridColumn: {md: 'span 2'}}}>
                                                                        <FieldLabel>Cidade</FieldLabel>
                                                                        <TextField value={endereco.cidade || ''} onChange={(event) => updateEnderecoField(index, 'cidade', event.target.value)} placeholder="Cidade" disabled={viewOnly} fullWidth sx={inputSx} />
                                                                    </Box>
                                                                    <Box>
                                                                        <FieldLabel>UF</FieldLabel>
                                                                        <TextField select value={endereco.uf || ''} onChange={(event) => updateEnderecoField(index, 'uf', event.target.value)} disabled={viewOnly} fullWidth sx={inputSx}>
                                                                            <MenuItem value="">Selecione</MenuItem>
                                                                            {estadosBrasileiros.map((uf) => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
                                                                        </TextField>
                                                                    </Box>
                                                                    {['code_ibge_cidade', 'code_ibge_uf', 'latitude', 'longitude'].map((field) => (
                                                                        <Box key={field}>
                                                                            <FieldLabel>{field === 'code_ibge_cidade' ? 'Cód. IBGE Cidade' : field === 'code_ibge_uf' ? 'Cód. IBGE UF' : field === 'latitude' ? 'Latitude' : 'Longitude'}</FieldLabel>
                                                                            <TextField value={endereco[field] || ''} onChange={(event) => updateEnderecoField(index, field, event.target.value)} disabled={viewOnly} fullWidth sx={inputSx} />
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        ) : null}
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    </Stack>
                                )}
                            </Box>
                        </DialogContent>

                        <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 3, py: 2, borderTop: '1px solid #e5e7eb', bgcolor: '#f9fafb'}}>
                            {viewOnly ? (
                                <Button variant="contained" color="inherit" onClick={() => setDialogOpen(false)}>
                                    Fechar
                                </Button>
                            ) : (
                                <>
                                    <Button variant="outlined" onClick={() => setDialogOpen(false)} disabled={saving}>
                                        Cancelar
                                    </Button>
                                    <Button type="submit" variant="contained" disabled={saving} sx={{bgcolor: '#0f766e', '&:hover': {bgcolor: '#115e59'}}}>
                                        {saving ? <CircularProgress size={20} color="inherit" /> : editing ? 'Salvar Alterações' : 'Criar Cliente'}
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Box>
                </Dialog>

                <Dialog open={!!confirmId} onClose={() => setConfirmId(null)}>
                    <Box sx={{p: 3, borderBottom: '1px solid #e5e7eb'}}>
                        <Typography sx={{fontSize: '1.25rem', fontWeight: 600}}>Excluir cliente</Typography>
                    </Box>
                    <DialogContent dividers>
                        Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita.
                    </DialogContent>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1.5, p: 2}}>
                        <Button onClick={() => setConfirmId(null)}>Cancelar</Button>
                        <Button variant="contained" color="error" onClick={onDelete}>Excluir</Button>
                    </Box>
                </Dialog>

                <Snackbar open={!!error} autoHideDuration={3500} onClose={() => setErrorPage('')}>
                    <Alert onClose={() => setErrorPage('')} severity="error" variant="filled">{error}</Alert>
                </Snackbar>
                <Snackbar open={!!warn} autoHideDuration={3500} onClose={() => setWarn('')}>
                    <Alert onClose={() => setWarn('')} severity="warning" variant="filled">{warn}</Alert>
                </Snackbar>
                <Snackbar open={!!ok} autoHideDuration={2500} onClose={() => setOk('')}>
                    <Alert onClose={() => setOk('')} severity="success" variant="filled">{ok}</Alert>
                </Snackbar>
            </Box>
        </Box>
    );
}
